import React, { useState } from "react";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import "./tabs.scss";
import type { DriverTabFilter } from "../hooks/usePickupAssignmentDriversColumn";

interface TabDef {
  id: string;
  baseLabel: string;
  count: number | undefined;
  content: React.ReactNode;
}

export interface TabsComponentProps {
  /**
   * `drivers`: use `renderDriversPanel` so only the active tab mounts (same as unassigned).
   * Legacy `all` / `atRisk` / `late` props mount all three panels at once and are very expensive.
   * `unassigned`: one active panel + API filter per tab — only the selected tab mounts (single fetch).
   */
  variant?: "drivers" | "unassigned";
  all?: React.ReactNode;
  atRisk?: React.ReactNode;
  late?: React.ReactNode;
  /** Drivers column: render body for the active tab only (avoids 3× DraggableList + RBD trees). */
  renderDriversPanel?: (tab: DriverTabFilter) => React.ReactNode;
  /** Unassigned column: render body for the active tab only. */
  renderUnassignedPanel?: (tab: DriverTabFilter) => React.ReactNode;
  /** Tab labels include counts: All (10). */
  tabCounts?: { all: number; atRisk: number; late: number };
  /** Controlled drivers tab (Pickup Assignment V2). When set with `onDriversTabChange`, tab state lives in the parent. */
  driversTab?: DriverTabFilter;
  onDriversTabChange?: (tab: DriverTabFilter) => void;
  /** Controlled unassigned tab (Pickup Assignment V2). */
  unassignedTab?: DriverTabFilter;
  onUnassignedTabChange?: (tab: DriverTabFilter) => void;
}

function formatTabLabel(base: string, count: number | undefined): string {
  if (count === undefined) return base;
  return `${base} (${count})`;
}

const TAB_ORDER: DriverTabFilter[] = ["all", "atRisk", "late"];

const TabsComponent: React.FC<TabsComponentProps> = (props) => {
  const {
    variant = "drivers",
    tabCounts,
    renderUnassignedPanel,
    renderDriversPanel,
    driversTab,
    onDriversTabChange,
    unassignedTab,
    onUnassignedTabChange,
  } = props;
  const [unassignedIndex, setUnassignedIndex] = useState(0);
  const [driversIndex, setDriversIndex] = useState(0);

  if (variant === "drivers" && renderDriversPanel) {
    const driversControlled =
      driversTab != null && onDriversTabChange != null;
    const driversSelectedIndex = driversControlled
      ? Math.max(0, TAB_ORDER.indexOf(driversTab))
      : driversIndex;

    const tabMeta = [
      { id: "all", baseLabel: "All", count: tabCounts?.all },
      { id: "atRisk", baseLabel: "At Risk", count: tabCounts?.atRisk },
      { id: "late", baseLabel: "Late", count: tabCounts?.late },
    ];

    return (
      <div className="address-details xgs-pickup-delivery-details">
        <Tabs
          selectedIndex={driversSelectedIndex}
          onSelect={(i) => {
            if (driversControlled) {
              onDriversTabChange(TAB_ORDER[i]);
            } else {
              setDriversIndex(i);
            }
          }}
        >
          <TabList>
            {tabMeta.map((t) => (
              <Tab key={t.id}>{formatTabLabel(t.baseLabel, t.count)}</Tab>
            ))}
          </TabList>
          {TAB_ORDER.map((tabId, i) => (
            <TabPanel key={tabId}>
              {driversSelectedIndex === i ? renderDriversPanel(tabId) : null}
            </TabPanel>
          ))}
        </Tabs>
      </div>
    );
  }

  if (variant === "unassigned" && renderUnassignedPanel) {
    const unassignedControlled =
      unassignedTab != null && onUnassignedTabChange != null;
    const unassignedSelectedIndex = unassignedControlled
      ? Math.max(0, TAB_ORDER.indexOf(unassignedTab))
      : unassignedIndex;

    const tabMeta = [
      { id: "all", baseLabel: "All", count: tabCounts?.all },
      { id: "atRisk", baseLabel: "At Risk", count: tabCounts?.atRisk },
      { id: "late", baseLabel: "Late", count: tabCounts?.late },
    ];

    return (
      <div className="address-details xgs-pickup-delivery-details">
        <Tabs
          selectedIndex={unassignedSelectedIndex}
          onSelect={(i) => {
            if (unassignedControlled) {
              onUnassignedTabChange(TAB_ORDER[i]);
            } else {
              setUnassignedIndex(i);
            }
          }}
        >
          <TabList>
            {tabMeta.map((t) => (
              <Tab key={t.id}>{formatTabLabel(t.baseLabel, t.count)}</Tab>
            ))}
          </TabList>
          {TAB_ORDER.map((tabId, i) => (
            <TabPanel key={tabId}>
              {unassignedSelectedIndex === i ? renderUnassignedPanel(tabId) : null}
            </TabPanel>
          ))}
        </Tabs>
      </div>
    );
  }

  const { all, atRisk, late } = props;
  const tabData: TabDef[] = [
    { id: "all", baseLabel: "All", count: tabCounts?.all, content: all },
    { id: "atRisk", baseLabel: "At Risk", count: tabCounts?.atRisk, content: atRisk },
    { id: "late", baseLabel: "Late", count: tabCounts?.late, content: late },
  ];

  return (
    <div className="address-details xgs-pickup-delivery-details">
      <Tabs defaultIndex={0}>
        <TabList>
          {tabData.map((tab) => (
            <Tab key={tab.id}>{formatTabLabel(tab.baseLabel, tab.count)}</Tab>
          ))}
        </TabList>

        {tabData.map((tab) => (
          <TabPanel key={tab.id}>{tab.content}</TabPanel>
        ))}
      </Tabs>
    </div>
  );
};

export default TabsComponent;
